require('./style.scss');
import {READY} from './constants.json';
import APIController from './api';
import EventEmitter from '../helpers/EventEmmiter';
import { generateUUID, getPathId } from '../helpers/common';
import Subtitle from './subtitle';
import RealtimeTourGuide from './RealtimeTourGuide';
import LeadGenerationPopup from './ui/LeadGenerationPopup';
import AvatarStartPopup from './ui/AvatarStartPopup';
import showAvatarNotice from './ui/AvatarNotice';
import { setupChromaKey } from './chromaKey';
import { MIC_CONSTRAINTS } from './ConnectionManager';
import { hasLeadDetails, savedPhone, savedLead, rememberLead } from './leadMemory';
import { resolveRoomText } from './utils/scriptUtils';
import ScreenWakeLock from './wakeLock';

const CONV_FLUSH_MS = 3000; // max delay before queued conversation records are sent
const CONV_RETRY_MS = 1500; // a failed conversation batch is retried once after this

export default class Avatar extends EventEmitter{
    _class = 'avatar';
    _node = null;
    initListeners = false;
    mode='';

    constructor(wrapper, {lang, translateService, modalService, config={}}) {
        super();
        this._wrapper = wrapper;
        this._translateService = translateService;
        this._modalService = modalService;
        this._json = config.avatar_json ? JSON.parse(config.avatar_json) : {};
        this._json.originSystemPrompt = this.updateSystemPromt(this._json, config);
        this._json.rooms = this.updateRoomsPromt(this._json, config);
        this._json.id = generateUUID();
        this._json.agentID = this._json.agentID || (config.agent.agentID || null);
        const _tourMatch = window.location.hash.match(/tours\/(\d+)/);
        this._json.tourID = !config.editable ? getPathId() : (_tourMatch ? _tourMatch[1] : null);
        this.config = config;
        if (!this._json.language_code) {
          this._json.language_code = this._json.defaultLang || lang || 'en';
        }

        // Opened from the AI widget's tour modal: ?avatar=play starts the
        // guide without the play button, ?esv=<handoff token> brings the
        // visitor the widget already knows (no lead form, no re-asking).
        const params = new URLSearchParams(window.location.search);
        this._autoPlay = params.get('avatar') === 'play';
        this._knownVisitor = false;
        this._visitorInfo = '';
        const esv = params.get('esv');
        this._handoffReady = esv ? this._loadHandoff(esv) : Promise.resolve();
        console.log("📝 Updated system prompt:", this._json.systemPrompt);     

        this._wakeLock = new ScreenWakeLock(); // screen stays on during a session (AV-43)

        // Conversation saves are batched: one POST /conversation per turn
        // instead of one per message (see _queueConversation)
        this._convQueue = [];
        this._convTimer = null;
        this._onPageHide = () => this._flushConversation({ keepalive: true });
        this._onVisibility = () => {
          if (document.visibilityState === 'hidden') this._flushConversation({ keepalive: true });
        };
        window.addEventListener('pagehide', this._onPageHide);
        document.addEventListener('visibilitychange', this._onVisibility);
    }

    // Queue one conversation record. The turn's final assistant answer flushes
    // straight away; anything else (user transcript, interim "Let me check.")
    // goes out with it, or after CONV_FLUSH_MS at the latest.
    _queueConversation(sessionId, msg, flushNow = false) {
      this._convQueue.push({ sessionId, msg });
      if (flushNow) {
        this._flushConversation();
      } else if (!this._convTimer) {
        this._convTimer = setTimeout(() => this._flushConversation(), CONV_FLUSH_MS);
      }
    }

    // Send everything queued, one request per session id. A failed batch is
    // retried once (records keep their created_at, so order is preserved
    // server-side), then dropped with an error log. keepalive is used when the
    // page is going away — sendBeacon can't carry the X-Visitor-Id header.
    _flushConversation({ keepalive = false } = {}) {
      clearTimeout(this._convTimer);
      this._convTimer = null;
      if (!this._convQueue || !this._convQueue.length) return;
      const items = this._convQueue.splice(0);
      const bySession = new Map();
      items.forEach(({ sessionId, msg }) => {
        if (!bySession.has(sessionId)) bySession.set(sessionId, []);
        bySession.get(sessionId).push(msg);
      });
      const userId = this._userId;
      const clientId = this._json?.client_id || null;
      bySession.forEach((messages, sessionId) => {
        const send = () => APIController.saveConversation(sessionId, messages, userId, clientId, { keepalive });
        send().catch(() => {
          if (keepalive) {
            // page is unloading — a delayed retry would never run
            return send().catch(err => console.error(`[avatar] conversation save failed twice, ${messages.length} message(s) lost`, err));
          }
          setTimeout(() => {
            send().catch(err => console.error(`[avatar] conversation save failed twice, ${messages.length} message(s) lost`, err));
          }, CONV_RETRY_MS);
        });
      });
    }

    // avatar_json from the tour config: a JSON string or a URL to it. Empty,
    // "null" and "{}" mean the tour has no avatar.
    static isConfigured(avatarJson) {
        const v = typeof avatarJson === 'string' ? avatarJson.trim() : '';
        return !!v && v !== 'null' && v !== '{}';
    }

    isMuseum() {
        return this._json.type === 'MUSEUM';
    }

    // Museum tour with a scripted guide flow configured
    hasMuseumFlow() {
        return this.isMuseum() && (this._json.guide || []).length > 0;
    }

    isStarted() {
        return this.mode === READY;
    }

    isConnected() {
        return !!this.guide;
    }

    // Visitor details from a widget handoff token. Fills the lead the form
    // would have collected and the history the widget's AI was given.
    async _loadHandoff(token) {
      try {
        const v = await APIController.getTourVisitor(token);
        if (!v || v.user_id == null) return;
        this._userId = v.user_id;
        this._visitorInfo = v.visitor_info || '';
        this._json.lead = {
          ...(this._json.lead || {}),
          first_name: v.first_name || '',
          surname: v.surname || '',
          phone: v.phone || '',
          email: v.email || '',
          lang: this._json.language_code,
        };
        this._knownVisitor = !!(v.first_name || v.phone || v.email);
        console.log(`[avatar] visitor from widget handoff: user ${v.user_id}${this._knownVisitor ? '' : ' (no contact details)'}`);
      } catch (e) {
        console.warn('[avatar] widget handoff lookup failed — falling back to the lead form', e);
      }
    }

    // Resolves true to start the guide, false when the visitor closed the form.
    async showLeadPopup() {
      await this._handoffReady;
      if (this._knownVisitor) return true; // the widget already has this visitor's details
      this._leadType = this._json.type || null;
      this._showLeadPopup = !!this._leadType && this._json.lead_generation !== false;
      if (!this._showLeadPopup) return true;
      // Filled in already on this page (Pause → Play) — don't ask again
      if (hasLeadDetails(this._json.lead)) return true;
      if (await this._recogniseReturningVisitor()) return true;
      return new Promise((res) => {
        this._leadPopup = new LeadGenerationPopup({
          avatarJson: this._json,
          type: this._leadType,
          translateService: this._translateService,
          onCancel: () => {
            this._leadPopup = null;
            res(false);
          },
          onComplete: async (updatedJson) => {
            this._leadPopup = null;
            this._json = updatedJson || this._json;
            const newLang = updatedJson?.lead?.lang;
            if (newLang) this._json.language_code = newLang;
            if (hasLeadDetails(this._json.lead)) {
              try {
                await this._saveLeadInfo(this._json.lead || {});
                this._sendAnalitics('avatar_lead_captured', `${this._json.tourID}|${this._json.type}|${this._json.lead?.email || ''}`);
              } catch(e) {
                console.warn('Lead save failed:', e);
              }
            }
            res(true);
          }
        });
      });
    }

    // A visitor who filled the form before (es360_phone cookie) and is still
    // known to the backend skips it — no second visitor record.
    async _recogniseReturningVisitor() {
      const phone = savedPhone();
      if (!phone) return false;
      try {
        const u = await APIController.getUserByPhone(phone);
        if (!u || u.user_id == null) return false;
        this._userId = u.user_id;
        const saved = savedLead() || {};
        this._json.lead = { ...saved, phone };
        const langs = this._json.langs || [];
        if (saved.lang && (!langs.length || langs.includes(saved.lang))) this._json.language_code = saved.lang;
        console.log(`[avatar] returning visitor (user ${u.user_id}) — skipping the lead form`);
        return true;
      } catch (_) {
        return false;
      }
    }

    _sendAnalitics(action, label) {
        try {
            if (typeof ga === 'function') {
                ga('send', {
                    hitType: 'event',
                    eventCategory: 'avatar',
                    eventAction: action,
                    eventLabel: label || this._json?.tourID || window.location.href,
                });
            }
        } catch (_) {}
    }

    async _saveLeadInfo(lead) {
      const payload = {
        tourID: this._json.tourID || '',
        agentID: this._json.agentID || '',
        type: this._json.type || '',
        source_url: this._json.lead_gen_url || '',
        // lets the backend also list the lead under the client's Admin → Leads (AV-07)
        client_id: this._json.client_id ?? null,
        lead: lead || {},
        guide: this._json.guide || [],
      };
      const result = await APIController.genLead(payload, this._json.lead_gen_url || '');
      rememberLead(lead);
      if (result && result.user_id != null) {
        this._userId = result.user_id;
      }
      return result;
    }

    _getCookiePhone() {
      return savedPhone();
    }

    async initMainListener () {
      this.conversationActive = true;
      await this.initAudioSpech();
    }

    async selectMic() {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(d => d.kind === "audioinput");
      console.log("Available mics:", mics);

      if (mics.length === 0) {
        alert(this._translateService.translate("E3_NO_MIC_FOUND"));
        throw new Error(this._translateService.translate("E3_NO_MIC_FOUND"));
      }

      let deviceId = mics[0].deviceId;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId }
      });

      return stream;
    }

    _extractHostpotsSystemPrompt(hotspots) {
      if(!hotspots) return "";

       return Object.values(hotspots)
        .map(h => `(hotspotID - ${h.id} - title - ${h.title}; type - ${h.type}; systemPrompt - ${h.systemPrompt})`)
        .join(" | ");
    }

    updateRoomsPromt(json, config) {
      const defLang = json.defaultLang || 'en';

      // Resolve message text and audio-URL map from both old and new formats.
      // New format: message = {lang: {text, audio}}
      // Old format: message = string, link = {lang: url} | string
      const resolveRoomTextLegacy = (msg, lang) => {
        if (!msg) return '';
        if (typeof msg === 'string') return msg;
        return msg[lang]?.text || msg[defLang]?.text || Object.values(msg)[0]?.text || '';
      };
      const resolveRoomLink = (msg, link, lang) => {
        if (msg && typeof msg === 'object') {
          // new format — build {lang: audioUrl} map so RealtimeTourGuide.play() can resolve by active lang
          const audioMap = {};
          for (const [l, v] of Object.entries(msg)) {
            if (v?.audio) audioMap[l] = v.audio;
          }
          return Object.keys(audioMap).length ? audioMap : null;
        }
        // old format
        if (!link) return null;
        if (typeof link === 'string') return link;
        if (typeof link === 'object') return link;
        return null;
      };

      const info = (config.scenes || []).map(scene => {
        const roomData = (json.rooms || {})[scene.id] || {};
        return {
          roomID: scene.id,
          name: scene.name,
          title: scene.title,
          message: resolveRoomTextLegacy(roomData.message, defLang),
          // per-language map ({fr: {text, audio}}) — resolved with the guide's
          // current language when spoken (resolveRoomText in utils/scriptUtils)
          messages: (roomData.message && typeof roomData.message === 'object') ? roomData.message : null,
          systemPrompt: roomData.systemPrompt || "",
          link: resolveRoomLink(roomData.message, roomData.link, defLang),
          describe: roomData.describe || "",
          hotspots: (Object.values(roomData?.hotspots || {}) || []).map(h => ({
            hotspotID: h.id || null,
            title: h.title || null,
            systemPrompt: h.systemPrompt || "",
            type: h.type || null,
          }))
        };
      });

      return info;
    }

    updateSystemPromt(json, config) {
      return `${json.systemPrompt}`;        
    }

    async initAudioSpech() {
        try {
          if(this.guide) {
            return false;
          }

          this._pendingTiming = {};
          // Timings belong to one turn: asr_ms to the user message, the rest to
          // the assistant answer. A user message only takes asr_ms — llm_ms can
          // arrive before the transcript, and wiping it there lost it. Interim
          // assistant lines (the "Let me check." before a lookup) take nothing,
          // so the post-lookup answer carries the turn's llm/rag/e2e timings.
          const buildConversationItem = ({ words, role, interim = false }) => {
            const t = this._pendingTiming;
            if (role === 'user') {
              delete this._pendingTiming.asr_ms;
            } else if (!interim) {
              this._pendingTiming = {};
            }
            const use = interim ? {} : t;
            return {
              words,
              role,
              voice: this._json?.ttsModel ?? null,
              lang: this.guide.language,
              created_at: Date.now(),
              asr_ms:     role === 'user'      ? (use.asr_ms      ?? null) : null,
              llm_ms:     role === 'assistant' ? (use.llm_ms      ?? null) : null,
              rag_ms:     role === 'assistant' ? (use.rag_ms      ?? null) : null,
              rag_source: role === 'assistant' ? (use.rag_source  ?? null) : null,
              // speech_stopped → first delta of the answering response (CONTRACT
              // with the avatar backend: optional integer, stored per message)
              e2e_ms:     role === 'assistant' ? (use.e2e_ms      ?? null) : null,
              // per-source lookup timings, e.g. "backendKB=120ms,roomsData=1840ms;stop=grace"
              rag_detail: role === 'assistant' ? (use.rag_detail  ?? null) : null,
            };
          };
          const scenes = this.config.scenes.map((obj) => {
            const room = this._json.rooms.find((room) => room.roomID === obj.id);
            obj.description = room.systemPrompt;
            obj.message = room.message;
            obj.messages = room.messages;
            obj.hotspots = room.hotspots;
            obj.link = room.link;
            obj.describe = room.describe;

            return obj;
          })
          const session_id = generateUUID();

          // Identify returning user via phone cookie
          if (!this._userId) {
            const savedPhone = this._getCookiePhone();
            if (savedPhone) {
              try {
                const u = await APIController.getUserByPhone(savedPhone);
                if (u && u.user_id != null) this._userId = u.user_id;
              } catch (_) {}
            }
          }

          const guide = new RealtimeTourGuide(this._json.language_code, this._unlockedAudio || null);
          this.guide = guide;
          // The microphone was already granted in _togglePlay — reuse that stream
          if (this._micStream) {
            guide.connection.micStream = this._micStream;
            this._micStream = null;
          }
          if (this._json.heygen_enabled && this._json.heygenAvatarId && this._heygenVideoEl) {
            console.log(`[heygen][mode] avatar config heygenMode=${JSON.stringify(this._json.heygenMode)} → using ${this._json.heygenMode === 'LITE' ? 'LITE' : 'FULL'} (re-save the avatar modal if this is missing)`);
            this.guide.initHeyGen(this._heygenVideoEl, this._json.heygenAvatarId, this._json.heygenQuality || 'medium', !!this._json.heygenSandbox, this._json.heygenMode || 'FULL');
            this.guide.on('heygen:ready', () => {
              // Chroma-key the green studio background onto the canvas; the
              // video keeps playing hidden underneath (the canvas needs live
              // frames). Falls back to the raw video if 2D canvas is missing
              // or the tour explicitly opts out (heygenChromaKey: false).
              if (this._json.heygenChromaKey !== false && this._heygenCanvasEl) {
                this._stopChromaKey = setupChromaKey(this._heygenVideoEl, this._heygenCanvasEl);
              }
              if (this._stopChromaKey) {
                this._heygenVideoEl.style.visibility = 'hidden';
                this._heygenVideoEl.classList.remove('hidden');
                this._heygenCanvasEl.classList.remove('hidden');
              } else {
                this._heygenVideoEl.classList.remove('hidden');
              }
            });
            // Avatar failed (new-session error, LiveKit join/drop, timeout):
            // the guide continues voice-only — hide the video tile.
            this.guide.on('heygen:unavailable', () => this._hideHeyGenVideo());
            // AV-41: the browser blocked the avatar's sound — the tap on this
            // prompt (any tap) unlocks it; it goes away once sound plays
            this.guide.on('heygen:audio_blocked', () => this._showSoundPrompt());
            this.guide.on('heygen:audio_unlocked', () => this._hideSoundPrompt());
          }
          this._fireEvent('guide:connected');
          this._sendAnalitics('avatar_connected', `${this._json.tourID}|${this._json.type || ''}|${this._json.language_code || ''}`);
          this.guide.setSceneId(this._sceneID);
          // once per Avatar, not per session — it used to pile up over Pause/Play
          if (!this._onSetSceneWired) {
            this._onSetSceneWired = true;
            this.on('on-set-scene', (sceneID) => {
              if (!this.guide) return;
              this.guide.setSceneId(sceneID);
              this.guide.sendSessionUpdate(null);
            })
          }
          this.guide.on('on-change-scene', ({room_id, position}, call_id) => {
            this._sendAnalitics('avatar_scene_change', `${room_id}|${this._json.tourID}`);
            this._fireEvent('on-change-scene', room_id, position, call_id);
          })
          this.guide.on('on-open-info', ({room_id, hotspot_id}) => {
            this._fireEvent('on-open-info', {sceneID: room_id, hotspotID: hotspot_id});
          })
          this.guide.on('open-hotspot', ({hotspot_id, scene_id, skip_navigate, wait_for_close}) => {
            this._fireEvent('on-open-info', {
              sceneID:        scene_id,
              hotspotID:      hotspot_id,
              skipNavigation: skip_navigate,
              waitForClose:   wait_for_close
            });
          })
          this.guide.on('close-hotspot', () => {
            this._fireEvent('close-hotspot');
          })
          // New visitor turn — drop timings left over from a turn whose answer
          // never got saved (interrupted, suppressed post-tool response).
          this.guide.on('timing:turn_start', () => { this._pendingTiming = {}; });
          this.guide.on('timing:asr', (ms) => { this._pendingTiming.asr_ms = ms; });
          this.guide.on('timing:e2e', (ms) => { this._pendingTiming.e2e_ms = ms; });
          this.guide.on('timing:llm', (ms) => { this._pendingTiming.llm_ms = ms; });
          this.guide.on('timing:rag', ({ rag_ms, rag_source, rag_detail }) => {
            this._pendingTiming.rag_ms = rag_ms;
            this._pendingTiming.rag_source = rag_source;
            this._pendingTiming.rag_detail = rag_detail;
          });
          this.guide.on('transcript:user', async (transcript) => {
            console.log('transcript:user', transcript);
            const msg = buildConversationItem({
                words: transcript,
                role: 'user',
              })
            this._queueConversation(session_id, msg);
          })
          this.guide.on('transcript:assistant_interim', (transcript) => {
            console.log('transcript:assistant (interim)', transcript);
            const msg = buildConversationItem({ words: transcript, role: 'assistant', interim: true });
            this._queueConversation(session_id, msg);
          })
          this.guide.on('transcript:assistant', async (transcript) => {
            console.log('transcript:assistant', transcript);
            const msg = buildConversationItem({
                words: transcript,
                role: 'assistant',
              })
            // the turn's answer — send the whole turn now
            this._queueConversation(session_id, msg, true);
          })
          this.guide.on('connection:status', (status) => {
            console.log('connection:status', status);
            // covers every session end, incl. idle timeout / audio limit
            if (status === 'disconnected') this._flushConversation();
            // The guide ended the session itself (idle timeout, audio limit,
            // "goodbye") — put the button back to Play.
            if (status === 'disconnected' && this.guide === guide) this._endSession({ alreadyDisconnected: true });
          })
          this.guide.on('connection:error', (err) => {
            if (this.guide !== guide) return;
            console.warn('[avatar] guide connection failed — ending the session', err);
            if (err && err.lost) {
              this._notice('AVATAR_CONNECTION_LOST', 'The connection to the guide was lost. Press Play to start again.');
            } else {
              this._notice('AVATAR_CONNECT_ERROR', 'The guide could not connect. Please press Play to try again.');
            }
            this._endSession();
          })
          this.guide.on('voice:listening', () => {
            console.log('voice:listening');
          })
          this.guide.on('on-close', () => {
            this._sendAnalitics('avatar_close', `${this._json.tourID}|${this._json.type || ''}`);
            this._fireEvent('on-change-mode', 'close');
            if (this.guide && typeof this.guide.destroy === 'function') {
              this.guide.destroy();
            }
          })
          this.guide.on('on-change-mode', ({mode}) => {
            this._fireEvent('on-change-mode', mode);
          })
          this.guide.on('rotate-view', ({ yaw, pitch, hfov, duration }) => {
            this._fireEvent('rotate-view', { yaw, pitch, hfov, duration });
          })
          this.guide.on('play-render-zone', (data) => {
            this._fireEvent('play-render-zone', data);
          })
          this.guide.on('render-zone:cancel', () => {
            this._fireEvent('render-zone:cancel');
          })
          this.guide.on('agent:disabled', () => {
            console.warn('[avatar] agent disabled by server for this KB key');
            this._sendAnalitics('avatar_agent_disabled', `${this._json.tourID}|${this._json.kbSelect || ''}`);
            if (this.isMuseum()) {
              this.guide.connectMuseumOnly(this._node);
            } else if (this.guide === guide) {
              // No AI for this tour (switched off on the server): say so and release the mic
              this._notice('AVATAR_UNAVAILABLE', 'The AI guide is not available for this tour right now.');
              this._endSession();
            }
          })
          this.guide.on('museum:started', () => this._fireEvent('museum:started'));
          this.guide.on('museum:completed', () => {
            this._fireEvent('museum:completed');
            this._sendAnalitics('avatar_museum_complete', `${this._json.tourID}|${this._json.language_code || ''}`);
          })
          this.guide.setTourData({
            ...this.config,
            scenes,
            systemPrompt: this._json.originSystemPrompt,
            ragLinks: this._json.ragLinks,
            ttsModel: this._json.ttsModel,
            roomsData: this._json.roomsData,
            kbSelect: this._json.kbSelect || "",
            source: this._json.source || "",
            lead: this._json.lead || {},
            visitorInfo: this._visitorInfo || '',
            guide: this._json.guide || [],
            museum_show_timeline: this._json.museum_show_timeline !== false,
            lookAroundAudio: this._json.lookAroundAudio || {},
            micSensitivity: this._json.micSensitivity || '',
            voiceAccent: this._json.voiceAccent || '',
            type: this._json.type || '',
            tourID: this._json.tourID || '',
            companyID: this.config.agent.companyID || '',
          });
          const agentEnabled = this._json.agent_enabled !== false;
          if (!agentEnabled && this.isMuseum()) {
            this.guide.connectMuseumOnly(this._node);
          } else if (agentEnabled) {
            this.guide.connect(this._node);
          }
        } catch(e) {
          // a half-started session would leave the button on Pause with nothing behind it
          console.error('[avatar] session start failed', e);
          if (this.guide) {
            this._endSession();
          } else {
            this.initListeners = false;
            this._wakeLock.disable();
            if (this._micStream) {
              this._micStream.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
              this._micStream = null;
            }
            this._setStatus('play');
          }
          this._notice('AVATAR_CONNECT_ERROR', 'The guide could not connect. Please press Play to try again.');
        }
    }

    _hideHeyGenVideo() {
      if (this._stopChromaKey) {
        try { this._stopChromaKey(); } catch (_) {}
        this._stopChromaKey = null;
      }
      [this._heygenVideoEl, this._heygenCanvasEl].forEach(el => {
        if (el) el.classList.add('hidden');
      });
      if (this._heygenVideoEl) this._heygenVideoEl.style.visibility = '';
    }

    clearJSON(_json) {
      const {
          ttsModel,
          systemPrompt,
          ragLinks,
          kbSelect,
          rooms,     
          roomsData,
          originSystemPrompt,   
          systemPrompt: _,
          ...rest
        } = _json;

      return {
        ...rest,
        model: ttsModel,
        system_prompt: systemPrompt || "",
        knowledge_base: kbSelect || ""
      };
    }

    async _setStatus(status) {
        const controls = this._controls;
        if (!controls) return;

        // if (!this.initListeners) {
        //     await this.initMainListener();
        //     this.initListeners = true;
        // }

        Object.values(controls).forEach(btn => btn.classList.add('hidden'));

        if (controls[status]) {
            controls[status].classList.remove('hidden');
        }

        this.conversationActive = (status === 'play');
    }

    async _setPlayButton() { 
        if (!this.initListeners) {
          // set before awaiting: a session that ends at once (connect error,
          // guide closing itself) resets it, and must not be overwritten after
          this.initListeners = true;
          await this.initMainListener();
        }

        this.conversationActive = !this.conversationActive;
    }

    setSceneId(sceneID) {
        this._sceneID = sceneID;
        this.mergeSystemPrompt(sceneID);
        this._fireEvent('on-set-scene', sceneID);
    }

    isNeedSpeak(sceneID) {
      const oldSystemPrompt = this.getRoomMessage(this._sceneID)?.systemPrompt;
      const newSystemPrompt = this.getRoomMessage(sceneID)?.systemPrompt;
      if (!newSystemPrompt || !oldSystemPrompt) {
        return true;
      }

      const regex = /\[\[#(.*?)\]\]/;

      const oldMatch = oldSystemPrompt.match(regex);
      const newMatch = newSystemPrompt.match(regex);

      if (oldMatch && newMatch) {
          const oldName = oldMatch[1]; 
          const newName = newMatch[1];

          if (oldName === newName) {
              return false; 
          } else {
              return true; 
          }
      }

      return true;
    }

    getRoomMessage(sceneID) {
      return this._json.rooms.find((obj) => obj.roomID === sceneID);
    }

    mergeSystemPrompt(sceneID, json=this._json) {
      const roomData = this.getRoomMessage(sceneID);
      const updateJSON = this.clearJSON(json);

      if (roomData && roomData.systemPrompt) {
        this._json.systemPrompt = `${json.originSystemPrompt} Current room info - ${roomData.systemPrompt} ${roomData.message}`;
        if (this.recognition) {
          this.recognition.updateJSON(updateJSON);
        }

        return updateJSON;
      }
      
      return updateJSON;
    }

    speak() {
      if(this.mode !== READY || !this.guide) {
        return ;
      }
      if (this._json.type === 'MUSEUM') {
        return;
      }
      const room = this.getRoomMessage(this._sceneID);
      const link = room?.link || null;
      const message = resolveRoomText(room, this.guide.bridge.getLanguage(), this._json.defaultLang || 'en') || null;

      // When video avatar is active, speak the room text via avatar instead of playing audio
      if (this.guide.heygenManager?.connected && message) {
        this.guide.heygenManager.speak(message);
        return;
      }

      if (link && this.guide.readyForPlay()) {
        return this.guide.play(link);
      } else if(message) {
        return this.guide.say(message);
      }

      this.guide.generateResponce(this._sceneID, '', message);
    }

    // The viewer finished loading a scene the guide (or the visitor) moved to.
    // A question asked together with the move ("what's the flooring like in
    // the bedroom, and take me there") is answered now, instead of the intro.
    onArrive(needSpeak = true) {
      if (this.guide && this.mode === READY && !this.isMuseum()) {
        const question = this.guide.state.takeArrivalQuestion(this._sceneID);
        if (question) {
          this.guide.answerOnArrival(question);
          return;
        }
      }
      if (needSpeak) this.speak();
    }

    _toggleControl(button) {
        const control = this._controls[button];
        if(control.classList.contains('hidden')) {
            control.classList.remove('hidden');
        }else {
            control.classList.add('hidden');
        }
    }

    createPanel(){
        const div = document.createElement('div');
        div.classList.add(this._class);

        this._node = div;

        this._createPanel();
    }

    _createControl = (callback, classNames, hidden) => {
        const el = document.createElement('button');
        if(Array.isArray(classNames)) {
            classNames.forEach((className) => {
                el.classList.add(`control-${this._class}-${className}`);
            })
            classNames = classNames[0];
        }else{
            el.classList.add(`control-${this._class}-${classNames}`);
        }
        el.classList.add('control');
        if(hidden) el.classList.add(hidden);

        el.addEventListener('click', callback);

        return {
            [classNames]: el
        };
    }

    // ?avatar=play: start by itself when the browser allows sound and the
    // microphone without a click; otherwise ask with one clear button.
    async _autoStart() {
      await this._handoffReady;
      if (this.isStarted()) return;
      if (await Avatar._canStartWithoutClick()) {
        console.log('[avatar] autoplay: sound and microphone allowed — starting');
        this._togglePlay();
        return;
      }
      console.log('[avatar] autoplay: browser needs a click — showing the start prompt');
      const name = (this._json.lead?.first_name || '').trim().split(/\s+/)[0] || '';
      this._startPopup = new AvatarStartPopup({
        name,
        translateService: this._translateService,
        onStart: () => { this._startPopup = null; this._togglePlay(); },
        onDismiss: () => { this._startPopup = null; },
      });
    }

    // Sound may play without a gesture (autoplay delegated by the widget's
    // iframe + a click on the host page) AND the microphone is already
    // granted, so starting won't pop an unexplained permission prompt.
    static async _canStartWithoutClick() {
      try {
        if (!navigator.permissions) return false;
        const mic = await navigator.permissions.query({ name: 'microphone' });
        if (mic.state !== 'granted') return false;
      } catch (_) {
        return false; // browsers that can't query the microphone
      }
      if (typeof navigator.getAutoplayPolicy === 'function') {
        return navigator.getAutoplayPolicy('audiocontext') === 'allowed';
      }
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const running = ctx.state === 'running';
        ctx.close().catch(() => {});
        return running;
      } catch (_) {
        return false;
      }
    }

    async _togglePlay() {
        if (this._startPopup) {
          this._startPopup.destroy();
          this._startPopup = null;
        }
        // iOS requires audio.play() to be called synchronously within a
        // user-gesture handler. Create and unlock a single Audio element here
        // (before any await) so it can be reused by MuseumAudioPlayer later.
        this._unlockedAudio = new Audio(
            'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA'
        );
        const p = this._unlockedAudio.play();
        if (p) p.then(() => this._unlockedAudio.pause()).catch(() => {});

        Avatar._unlockAudio();
        if (this._leadPopup || this._starting) return; // form already open / start in progress
        this._starting = true;
        try {
          const proceed = await this.showLeadPopup();
          if (!proceed) return; // form closed — stay on Play
          // Microphone before anything else starts (OpenAI session, video avatar
          // credits): blocked or missing → say so and stay on Play.
          if (this._json.agent_enabled !== false && !(await this._acquireMic())) return;
        } finally {
          this._starting = false;
        }
        this.mode = READY;
        this._wakeLock.enable();
        this._sendAnalitics('avatar_start', `${this._json.tourID}|${this._json.type || ''}|${this._json.language_code || ''}`);
        // Pause first: a start that fails at once puts it back to Play
        this._setStatus('pause');
        this._setPlayButton();
    }

    async _acquireMic() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error('getUserMedia unavailable'), { name: 'NotSupportedError' });
        this._micStream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
        return true;
      } catch (err) {
        console.warn('[avatar] microphone unavailable:', err && err.name, err);
        const name = err && err.name;
        if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          this._notice('E3_NO_MIC_FOUND', 'No microphone found. Connect a microphone and press Play again.');
        } else if (name === 'NotAllowedError' || name === 'SecurityError') {
          this._notice('AVATAR_MIC_BLOCKED', 'Microphone access is blocked. Allow the microphone for this site in your browser settings, then press Play again.');
        } else {
          this._notice('AVATAR_MIC_ERROR', 'The microphone could not be started. Make sure no other app is using it, then press Play again.');
        }
        this._setStatus('play');
        return false;
      }
    }

    _t(key, fallback) {
      const ts = this._translateService;
      const val = ts && typeof ts.translate === 'function' ? ts.translate(key) : null;
      return val && val !== key ? val : fallback;
    }

    _notice(key, fallback) {
      showAvatarNotice(this._t(key, fallback));
    }

    _showSoundPrompt() {
      if (this._soundPrompt) return;
      this._soundPrompt = showAvatarNotice(this._t('AVATAR_TAP_FOR_SOUND', 'Tap anywhere to hear the guide.'), { duration: 60000 });
    }

    _hideSoundPrompt() {
      if (!this._soundPrompt) return;
      this._soundPrompt.destroy();
      this._soundPrompt = null;
    }

    /**
     * iOS audio unlock — must be called synchronously inside a user-gesture
     * handler (click/touchend).  Two things to unlock:
     *
     *  1. HTMLAudioElement  — played by MuseumAudioPlayer (new Audio(url))
     *     Once any Audio element is played in a gesture, iOS allows all
     *     subsequent .play() calls on the page.
     *
     *  2. AudioContext — used by AudioMixer for the WebRTC AI voice stream.
     *     Resuming *any* AudioContext in a gesture causes iOS 13+ to
     *     auto-start all future AudioContext instances in "running" state.
     */
    static _unlockAudio() {
        // Unlock HTMLAudioElement
        try {
            const a = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
            const p = a.play();
            if (p) p.catch(() => {});
        } catch (_) {}

        // Unlock AudioContext (affects all future instances on this page)
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            const ctx = new AudioCtx();
            ctx.resume().then(() => {
                const buf = ctx.createBuffer(1, 1, 22050);
                const src = ctx.createBufferSource();
                src.buffer = buf;
                src.connect(ctx.destination);
                src.start(0);
                setTimeout(() => ctx.close().catch(() => {}), 500);
            }).catch(() => {});
        } catch (_) {}
    }

    _togglePause() {
      this._endSession();
    }

    // Every way a session ends — Pause, the guide closing itself, a failed
    // connect — goes through here: mic released, video hidden, button on Play.
    _endSession({ alreadyDisconnected = false } = {}) {
      const guide = this.guide;
      if (!guide) return;
      if (this.isMuseum() && guide.guideService) {
        try {
          const pos = guide.guideService.getPosition();
          sessionStorage.setItem(`museum_pos_${this._json.tourID}`, JSON.stringify(pos));
        } catch (_) {}
      }
      if (guide.museumAudioPlayer?.isPlaying) {
        guide.museumAudioPlayer.stop();
      }
      this._flushConversation();
      // cleared first: disconnect() reports 'disconnected' back synchronously
      this.guide = null;
      if (!alreadyDisconnected) guide.disconnect();
      this._hideHeyGenVideo(); // no frozen last frame of the video avatar
      this._hideSoundPrompt();
      this._wakeLock.disable();
      this._fireEvent('guide:disconnected');
      this.initListeners = false;
      this._setStatus('play');
    }

    pause() {
        this._togglePause();
    }

    _createPanel = () => {
        const panel = document.createElement('div');
        panel.classList.add('avatar-panel');

        if (this._json.heygen_enabled && this._json.heygenAvatarId) {
            this._heygenVideoEl = document.createElement('video');
            this._heygenVideoEl.autoplay = true;
            this._heygenVideoEl.playsInline = true;
            const ratioClass = this._json.heygenAspectRatio ? `avatar-heygen-video--${this._json.heygenAspectRatio}` : 'avatar-heygen-video--portrait';
            this._heygenVideoEl.classList.add('avatar-heygen-video', 'hidden', ratioClass);
            panel.appendChild(this._heygenVideoEl);
            // Same layout classes as the video — shows the chroma-keyed frames
            this._heygenCanvasEl = document.createElement('canvas');
            this._heygenCanvasEl.classList.add('avatar-heygen-video', 'hidden', ratioClass);
            panel.appendChild(this._heygenCanvasEl);
        }

        const controls = document.createElement('div');
        controls.classList.add('controls');

        const els = {
            ...this._createControl(() => this._togglePlay(), 'play'),
            ...this._createControl(() => this._togglePause(), 'pause', 'hidden'),
            ...this._createControl(() => {}, 'loading', 'hidden'), 
        }

        const canvas = document.createElement('canvas');
        canvas.id = 'audio-visualizer';

        for(let i in els) {
            controls.append(els[i]);
        }

        this._controls = els;
        panel.append(canvas);
        panel.append(controls);
        this._node.append(panel);
        this.canvas=canvas;
        this._subtitle = new Subtitle(this._wrapper);

        return panel;
    }

    _createLeadGenerationPopup() {}

    _addVideo(url, ext) {
        const source = document.createElement('source');
        let type = `video/${ext}`;
        if(ext === 'mp4') type =  `video/${ext}; codecs="hvc1"`
        source.setAttribute('type', type);
        source.src = `${url}`;

        return source;
    }


    init = async (sceneID = this._sceneID, lang = this._lang) => {        
        this._sceneID = sceneID;
        this._lang = lang;

        if(!this._wrapper.contains(this._node)) this._wrapper.appendChild(this._node);
        if (this._autoPlay && !this._autoStarted) {
          this._autoStarted = true;
          this._autoStart();
        }
    }

    applyClass(className){
        this._node.classList.add(className);
    }

    destroy() {
        this.conversationActive = false;
        this._flushConversation();
        window.removeEventListener('pagehide', this._onPageHide);
        document.removeEventListener('visibilitychange', this._onVisibility);

        if (this._startPopup) {
          this._startPopup.destroy();
          this._startPopup = null;
        }
        this._wakeLock.disable();

        if (this._leadPopup && typeof this._leadPopup.destroy === 'function') {
          try { this._leadPopup.destroy(); } catch (e) {}
          this._leadPopup = null;
        }

        if(this.guide) {
          const guide = this.guide;
          this.guide = null;
          guide.destroy();
        }
        if (this._micStream) {
          this._micStream.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });
          this._micStream = null;
        }

        if (this._node && this._wrapper && this._node.parentNode === this._wrapper) {
          this._wrapper.removeChild(this._node);
        }

        this._node = null;
        this._controls = null;
    }
}
